
import { Component, ErrorInfo, ReactNode } from 'react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { RefreshCw, AlertTriangle, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  resetOnPropsChange?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error | null;
  errorInfo?: ErrorInfo | null;
}

// Render props component for navigate functionality
const ErrorBoundaryFallback = ({
  error,
  resetError
}: {
  error: Error | null | undefined;
  resetError: () => void;
}) => {
  return (
    <Alert variant="destructive" className="m-4">
      <AlertTriangle className="h-5 w-5 mr-2" />
      <AlertTitle>Something went wrong</AlertTitle>
      <AlertDescription className="mt-2">
        {error?.message || 'An unexpected error occurred'}
      </AlertDescription>
      <div className="mt-4 flex gap-2">
        <Button
          variant="outline"
          onClick={resetError}
        >
          Try again
        </Button>
        <Button
          variant="default"
          onClick={() => window.location.reload()}
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          Reload page
        </Button>
        <Button
          variant="secondary"
          onClick={() => window.location.href = '/'}
        >
          <Home className="mr-2 h-4 w-4" />
          Home
        </Button>
      </div>
    </Alert>
  );
};

/**
 * Error Boundary component that catches JavaScript errors in its child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 */
class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null
  };

  // Used to reset the error boundary when props change if resetOnPropsChange is true
  public componentDidUpdate(prevProps: Props) {
    if (
      this.state.hasError &&
      this.props.resetOnPropsChange &&
      this.props.children !== prevProps.children
    ) {
      this.setState({
        hasError: false,
        error: null,
        errorInfo: null
      });
    }
  }

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error to the console
    console.error('Uncaught error in component:', error, errorInfo);
    
    this.setState({ errorInfo });
    
    // Production error logging - integrate with monitoring service
    if (process.env.NODE_ENV === 'production') {
      // Log error with structured data for monitoring
      const errorData = {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href,
        userAgent: navigator.userAgent
      };
      
      // You can integrate with services like Sentry, LogRocket, etc.
      console.error('Production error:', errorData);
      
      // Optional: Send to monitoring service
      // Example: window.Sentry?.captureException(error, { extra: errorData });
    }
    
    // Call the onError prop if it exists
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  public render() {
    if (this.state.hasError) {
      // Custom fallback UI
      return this.props.fallback || (
        <ErrorBoundaryFallback
          error={this.state.error}
          resetError={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
